import { Image } from "@shopify/hydrogen-react";
import { useHotkey } from "@tanstack/react-hotkeys";
import { Dialog, DialogContent, DialogTitle } from "@ui/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type LightboxImage = {
  id?: string | null;
  url: string;
  altText?: string | null;
  width?: number | null;
  height?: number | null;
};

type ProductImageLightboxProps = {
  images: Array<LightboxImage>;
  selectedIndex: number;
  productTitle: string;
  isOpen: boolean;
  zoomLevel: number;
  pan: { x: number; y: number };
  isPanning: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onPrevious: () => void;
  onNext: () => void;
  onResetTransform: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onWheel: (event: React.WheelEvent<HTMLElement>) => void;
  onMouseDown: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseMove: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: (event: React.TouchEvent<HTMLElement>) => void;
  onTouchMove: (event: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd: (event: React.TouchEvent<HTMLElement>) => void;
  onImageClick: () => void;
};

export function ProductImageLightbox({
  images,
  selectedIndex,
  productTitle,
  isOpen,
  zoomLevel,
  pan,
  isPanning,
  onOpenChange,
  onPrevious,
  onNext,
  onResetTransform,
  onZoomIn,
  onZoomOut,
  onWheel,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onImageClick,
}: Readonly<ProductImageLightboxProps>) {
  const { t } = useTranslation();
  const interactionAreaRef = useRef<HTMLButtonElement>(null);

  let cursorClass = "cursor-zoom-in";
  if (zoomLevel > 1) {
    cursorClass = isPanning ? "cursor-grabbing" : "cursor-grab";
  }

  useHotkey(
    "ArrowLeft",
    event => {
      event.preventDefault();
      onPrevious();
    },
    { enabled: isOpen }
  );

  useHotkey(
    "ArrowRight",
    event => {
      event.preventDefault();
      onNext();
    },
    { enabled: isOpen }
  );

  useHotkey(
    "Escape",
    event => {
      event.preventDefault();
      onOpenChange(false);
    },
    { enabled: isOpen }
  );

  useHotkey(
    { key: "=", shift: true },
    event => {
      event.preventDefault();
      onZoomIn();
    },
    { enabled: isOpen }
  );

  useHotkey(
    { key: "+" },
    event => {
      event.preventDefault();
      onZoomIn();
    },
    { enabled: isOpen }
  );

  useHotkey(
    { key: "NumpadAdd" },
    event => {
      event.preventDefault();
      onZoomIn();
    },
    { enabled: isOpen }
  );

  useHotkey(
    "-",
    event => {
      event.preventDefault();
      onZoomOut();
    },
    { enabled: isOpen }
  );

  useHotkey(
    "0",
    event => {
      event.preventDefault();
      onResetTransform();
    },
    { enabled: isOpen }
  );

  useEffect(() => {
    if (!isOpen) return;
    interactionAreaRef.current?.focus();
  }, [isOpen]);

  const currentImage = images[selectedIndex];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-black/10 p-0 border-0"
        fullscreen
        showCloseButton={false}
      >
        {/* Visually hidden title for screen readers (required by Radix Dialog) */}
        <DialogTitle className="sr-only">
          {productTitle} — {t("Common.imageViewer", "Image viewer")}
        </DialogTitle>
        <div className="relative flex h-full w-full items-center justify-center">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-text-7 absolute right-4 top-4 z-20 rounded-md bg-black/60 p-2 hover:bg-black/80"
            aria-label={t("Common.close", "Close")}
          >
            <X size={20} />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={onPrevious}
                className="text-text-7 absolute left-4 z-20 rounded-md bg-black/60 p-2 hover:bg-black/80"
                aria-label={t("Common.previousSlide", "Previous image")}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={onNext}
                className="text-text-7 absolute right-4 z-20 rounded-md bg-black/60 p-2 hover:bg-black/80"
                aria-label={t("Common.nextSlide", "Next image")}
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          <button
            type="button"
            aria-label={t("Common.imageZoomArea", "Image zoom and pan area")}
            className={`flex h-full w-full items-center justify-center overflow-hidden touch-none ${cursorClass}`}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            onClick={onImageClick}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            ref={interactionAreaRef}
            aria-keyshortcuts="ArrowLeft ArrowRight Escape + - 0"
          >
            <Image
              src={currentImage.url}
              alt={
                currentImage.altText ?? `${productTitle} ${selectedIndex + 1}`
              }
              width={currentImage.width ?? 2048}
              height={currentImage.height ?? 2048}
              fetchPriority="high"
              loading="eager"
              sizes="100vw"
              className={`h-full w-full object-contain motion-reduce:transition-none ${
                isPanning
                  ? "transition-none"
                  : "transition-transform duration-150 ease-out"
              }`}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomLevel})`,
              }}
              draggable={false}
            />
          </button>

          <div className="text-text-7 absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 rounded-full bg-black/60 px-4 py-2">
            <button
              type="button"
              onClick={onZoomOut}
              className="rounded p-1 hover:bg-black/40"
              aria-label={t("Common.zoomOut", "Zoom out")}
            >
              <ZoomOut size={18} />
            </button>

            <span className="text-small">
              {Math.round(zoomLevel * 100)}% · {selectedIndex + 1}/
              {images.length}
            </span>

            <button
              type="button"
              onClick={onZoomIn}
              className="rounded p-1 hover:bg-black/40"
              aria-label={t("Common.zoomIn", "Zoom in")}
            >
              <ZoomIn size={18} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
